/**
 * DestructController — /api/v1/destruct
 * Mirrors: src/Aidepo.Api/Controllers/DestructController.cs
 *
 * Two-phase bulk / cascade delete: preview → confirm.
 *   POST /api/v1/destruct/preview  — resolves the cascade, returns a confirm
 *                                    token (TTL 5 min) plus the exact list of
 *                                    records that would disappear.
 *   POST /api/v1/destruct/confirm  — executes the delete by token, writes a
 *                                    full-snapshot ChangeRecord (undo-able).
 *
 * hh-be3 (case-34/35): wired up against the real backend — the previous
 * forward-declared stub had the wrong contract and threw NOT_IMPLEMENTED.
 * The response types here are made structurally identical to the ones
 * `DestructPreviewScreen` already declares, so a preview response can be
 * handed straight to that pure component (see DestructCascadeScreen).
 */
import { apiPost } from '../client.js';
import type { Uuid } from '../types.js';

// ─── Preview ────────────────────────────────────────────────────────────────

/** At least one filter must be present, else the backend answers 422. */
export interface DestructPreviewRequest {
  readonly propertyId?: Uuid;
  readonly roomId?: Uuid;
  readonly itemIds?: readonly Uuid[];
  readonly equipmentIds?: readonly Uuid[];
}

export interface DestructAffectedProperty {
  readonly id: Uuid;
  readonly name: string;
}
export interface DestructAffectedRoom {
  readonly id: Uuid;
  readonly name: string;
  readonly propertyName: string;
}
export interface DestructAffectedItem {
  readonly id: Uuid;
  readonly name: string;
  readonly locationLabel: string;
}
export interface DestructAffectedEquipment {
  readonly id: Uuid;
  readonly name: string;
  readonly locationLabel: string;
}

export interface DestructPreviewAffected {
  readonly properties: readonly DestructAffectedProperty[];
  readonly rooms: readonly DestructAffectedRoom[];
  readonly items: readonly DestructAffectedItem[];
  readonly equipment: readonly DestructAffectedEquipment[];
}

export interface DestructPreviewCounts {
  readonly properties: number;
  readonly rooms: number;
  readonly items: number;
  readonly equipment: number;
}

export interface DestructPreviewResponse {
  readonly confirmToken: string;
  readonly expiresAt: string;
  readonly affected: DestructPreviewAffected;
  readonly counts: DestructPreviewCounts;
}

// ─── Confirm ────────────────────────────────────────────────────────────────

export interface DestructConfirmRequest {
  readonly confirmToken: string;
}

export interface DestructConfirmResponse {
  readonly deleted: DestructPreviewCounts;
  readonly changeRecordId: string;
}

/** POST /api/v1/destruct/preview — resolve the cascade and mint a confirm token. */
export function previewDestruct(req: DestructPreviewRequest): Promise<DestructPreviewResponse> {
  return apiPost<DestructPreviewResponse, DestructPreviewRequest>('/api/v1/destruct/preview', req);
}

/** POST /api/v1/destruct/confirm — execute the delete; 410 if the token expired/used. */
export function confirmDestruct(req: DestructConfirmRequest): Promise<DestructConfirmResponse> {
  return apiPost<DestructConfirmResponse, DestructConfirmRequest>('/api/v1/destruct/confirm', req);
}
